<template>
  <section class="monitor-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">{{ activeAgentLabel }}</p>
        <h1>Live Monitor</h1>
        <p class="subtitle">
          Real-time view of backend sessions, conversation activity, and recent call outcomes.
        </p>
      </div>

      <div class="header-actions">
        <button class="ghost-btn" @click="refresh" :disabled="loadingSessions">
          <RefreshCcw :size="16" :class="{ spin: loadingSessions }" />
          Refresh
        </button>
        <label class="toggle">
          <input type="checkbox" v-model="autoRefresh" />
          <span>Auto refresh</span>
        </label>
      </div>
    </header>

    <div v-if="error" class="error-banner">
      {{ error }}
    </div>

    <section class="kpi-grid">
      <div class="kpi-card">
        <span>Call status</span>
        <strong>{{ callStatus }}</strong>
      </div>
      <div class="kpi-card">
        <span>Booking</span>
        <strong>{{ bookingStatus }}</strong>
      </div>
      <div class="kpi-card">
        <span>Deposit</span>
        <strong>{{ depositStatus }}</strong>
      </div>
      <div class="kpi-card">
        <span>Calendar</span>
        <strong>{{ calendarStatus }}</strong>
      </div>
      <div class="kpi-card">
        <span>Call outcome</span>
        <strong>{{ callOutcome }}</strong>
      </div>
    </section>

    <div class="main-grid">
      <section class="panel sessions-panel">
        <div class="panel-header">
          <div>
            <h2>Recent sessions</h2>
            <p>Latest backend sessions for the active agent.</p>
          </div>
          <span class="pill">{{ sessions.length }}</span>
        </div>

        <div class="filters-row">
          <label>
            <span>Filter</span>
            <select v-model="sessionFilter">
              <option value="all">All sessions</option>
              <option value="today">Today</option>
              <option value="booked">Booked</option>
              <option value="contacts">Captured contacts</option>
              <option value="ended">Ended</option>
            </select>
          </label>

          <label>
            <span>Sort by</span>
            <select v-model="sortMode">
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>

        <div v-if="loadingSessions" class="loading-state">
          Loading live sessions...
        </div>

        <div v-else-if="sessions.length === 0" class="empty-state">
          No live sessions found for this agent.
        </div>

        <div v-else class="session-list">
          <button
            v-for="session in pagedSessions"
            :key="session.id"
            type="button"
            class="session-card"
            :class="{ selected: selectedSessionId === session.id }"
            @click="selectSession(session.id)"
          >
            <div>
              <strong>Session #{{ session.id }}</strong>
              <p>{{ sessionLabel(session) }}</p>
            </div>
            <span class="session-time">{{ formatDate(session.started_at) }}</span>
          </button>
        </div>

        <div v-if="pagination.totalPages > 1" class="pagination-row">
          <button class="pager-btn" :disabled="sessionPage === 1" @click="goToPage(sessionPage - 1)">
            Prev
          </button>
          <span>Page {{ sessionPage }} of {{ pagination.totalPages }}</span>
          <button class="pager-btn" :disabled="sessionPage >= pagination.totalPages" @click="goToPage(sessionPage + 1)">
            Next
          </button>
        </div>
      </section>

      <section class="panel transcript-panel">
        <div class="panel-header">
          <div>
            <h2>Conversation transcript</h2>
            <p>{{ selectedSessionLabel }}</p>
          </div>
        </div>

        <div class="transcript-scroll">
          <div v-if="transcriptLoading" class="loading-state">
            Loading transcript...
          </div>

          <div v-else-if="selectedMessages.length === 0" class="empty-state">
            No messages for this session.
          </div>

          <div v-else class="message-list">
            <article
              v-for="message in selectedMessages"
              :key="message.id"
              class="message-row"
              :class="message.sender"
            >
              <div class="avatar">{{ avatarLabel(message.sender) }}</div>
              <div class="message-card">
                <div class="message-top">
                  <strong>{{ roleLabel(message.sender) }}</strong>
                  <span>{{ formatDate(message.timestamp) }}</span>
                </div>
                <p>{{ message.text }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <aside class="panel detail-panel">
        <div class="panel-header">
          <div>
            <h2>Extracted caller data</h2>
            <p>Fields extracted from the conversation and stored on the session.</p>
          </div>
        </div>

        <div class="detail-grid">
          <div class="detail-card">
            <span>Name</span>
            <strong>{{ selectedSessionMeta.name }}</strong>
          </div>
          <div class="detail-card">
            <span>Phone</span>
            <strong>{{ selectedSessionMeta.phone }}</strong>
          </div>
          <div class="detail-card">
            <span>Service</span>
            <strong>{{ selectedSessionMeta.service }}</strong>
          </div>
          <div class="detail-card">
            <span>Appointment</span>
            <strong>{{ selectedSessionMeta.appointment }}</strong>
          </div>
        </div>

        <div class="session-meta-row">
          <div class="meta-chip">
            <span>Status</span>
            <strong>{{ selectedSessionStatus }}</strong>
          </div>
          <div class="meta-chip">
            <span>Started</span>
            <strong>{{ selectedSessionStarted }}</strong>
          </div>
        </div>
      </aside>

      <section class="panel timeline-panel">
        <div class="panel-header">
          <div>
            <h2>Action timeline</h2>
            <p>Derived from the selected session and transcript.</p>
          </div>
        </div>

        <div v-if="actionTimeline.length === 0" class="empty-state">
          No timeline events yet.
        </div>

        <div v-else class="timeline-list">
          <article v-for="item in actionTimeline" :key="item.uid" class="timeline-item">
            <div class="timeline-dot"></div>
            <div class="timeline-body">
              <div class="timeline-top">
                <strong>{{ item.title }}</strong>
                <span>{{ item.timeLabel }}</span>
              </div>
              <p>{{ item.description }}</p>
            </div>
          </article>
        </div>
      </section>

      <section class="panel agent-panel">
        <div class="panel-header">
          <div>
            <h2>Active agent</h2>
            <p>Backend agent linked to this live monitor.</p>
          </div>
        </div>

        <div class="agent-card" v-if="activeAgent">
          <strong>{{ activeAgent.name || activeAgent.business_name || `Agent #${activeAgent.id}` }}</strong>
          <p>{{ activeAgent.business_name || activeAgent.description || 'No description available.' }}</p>
          <div class="agent-meta">
            <span>{{ activeAgent.industry_key || 'Unknown industry' }}</span>
            <span>{{ activeAgent.default_language || 'Unknown language' }}</span>
          </div>
        </div>

        <div v-else class="empty-state">
          Active agent unavailable.
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Cookies from 'js-cookie'
import { RefreshCcw } from 'lucide-vue-next'
import * as api from '@/services/api.js'

const sessions = ref([])
const selectedMessages = ref([])
const selectedSessionId = ref(null)
const activeAgent = ref(null)
const loadingSessions = ref(true)
const transcriptLoading = ref(false)
const error = ref('')
const autoRefresh = ref(true)
const refreshTimer = ref(null)
const sessionFilter = ref('all')
const sortMode = ref('newest')
const sessionPage = ref(1)
const pageSize = 8
const pagination = ref({
  totalPages: 1,
  totalSessions: 0
})

const activeAgentId = computed(() => Cookies.get('active_agent_id'))

const activeAgentLabel = computed(() => {
  return activeAgentId.value ? `Agent #${activeAgentId.value}` : 'No active agent'
})

const selectedSession = computed(() => {
  return sessions.value.find(item => String(item.id) === String(selectedSessionId.value)) || null
})

const pagedSessions = computed(() => sessions.value)

const selectedSessionLabel = computed(() => {
  if (!selectedSession.value) return 'Select a session to inspect its transcript.'
  return selectedSession.value.session_label || `Session #${selectedSession.value.id}`
})

const selectedSessionStatus = computed(() => {
  if (!selectedSession.value) return '—'
  return selectedSession.value.status || (selectedSession.value.ended_at ? 'Ended' : 'Live')
})

const selectedSessionStarted = computed(() => {
  if (!selectedSession.value) return '—'
  return formatDate(selectedSession.value.started_at)
})

const selectedSessionMeta = computed(() => {
  const session = selectedSession.value || {}
  return {
    name: displayValue(session.name),
    phone: displayValue(session.phone),
    service: displayValue(session.service),
    appointment: displayValue(formatDate(session.scheduled_at))
  }
})

const callStatus = computed(() => {
  if (!selectedSession.value) return 'Waiting'
  if (selectedSession.value.ended_at) return 'Completed'
  return 'Live'
})

const bookingStatus = computed(() => {
  const session = selectedSession.value || {}
  if (Boolean(Number(session.booking_completed ?? 0)) || session.step === 'booking_completed') return 'Booked'
  if (session.scheduled_at || session.step === 'ready_to_book') return 'Booking in progress'
  return 'Not booked'
})

const depositStatus = computed(() => {
  const session = selectedSession.value || {}
  const transcript = selectedMessages.value.map(m => `${m.sender}: ${m.text}`.toLowerCase()).join(' ')

  if (session.step === 'deposit_link_sent' || transcript.includes('deposit link')) return 'Requested'
  if (session.step === 'deposit_pending' || transcript.includes('deposit')) return 'Requested'
  if (transcript.includes('paid') || transcript.includes('completed')) return 'Paid'
  return 'Not requested'
})

const calendarStatus = computed(() => {
  const session = selectedSession.value || {}
  if (session.scheduled_at) return 'Synced'
  return 'Not synced'
})

const callOutcome = computed(() => {
  if (!selectedSession.value) return 'No session'
  if (bookingStatus.value === 'Booked') {
    return depositStatus.value === 'Paid' ? 'Booked with deposit paid' : 'Booked'
  }
  if (depositStatus.value === 'Requested') return 'Deposit requested'
  if (selectedSession.value.ended_at) return 'Completed'
  return 'In progress'
})

const actionTimeline = computed(() => buildTimeline())

onMounted(() => {
  refresh({ preserveTranscript: false })
  refreshTimer.value = window.setInterval(() => {
    if (autoRefresh.value) refresh({ preserveTranscript: true })
  }, 15000)
})

onBeforeUnmount(() => {
  if (refreshTimer.value) window.clearInterval(refreshTimer.value)
})

async function refresh({ preserveTranscript = false } = {}) {
  loadingSessions.value = true
  error.value = ''

  try {
    const token = Cookies.get('token')
    if (!token) {
      error.value = 'You must be signed in to view the live monitor.'
      return
    }

    if (!activeAgentId.value) {
      error.value = 'No active agent selected.'
      return
    }

    const [agentResponse, response] = await Promise.all([
      api.agents.getById(activeAgentId.value).catch(() => null),
      api.liveMonitor.getSessions({
        agentId: activeAgentId.value,
        page: sessionPage.value,
        limit: pageSize,
        filter: sessionFilter.value,
        sort: sortMode.value
      })
    ])

    activeAgent.value = agentResponse || null
    sessions.value = normalizeList(response, ['sessions', 'data'])
    pagination.value = {
      totalPages: Number(response?.meta?.total_pages || 1),
      totalSessions: Number(response?.meta?.total_sessions || sessions.value.length)
    }

    if (sessions.value.length) {
      if (!preserveTranscript && !selectedSessionId.value) {
        selectedSessionId.value = sessions.value[0].id
        await loadSessionTranscript(selectedSessionId.value)
      }
    } else if (!preserveTranscript) {
      selectedSessionId.value = null
      selectedMessages.value = []
    }
  } catch (err) {
    error.value = err.message || 'Unable to load live monitor.'
    sessions.value = []
    selectedMessages.value = []
    activeAgent.value = null
  } finally {
    loadingSessions.value = false
  }
}

async function loadSessionTranscript(sessionId) {
  if (!sessionId) {
    selectedMessages.value = []
    return
  }

  transcriptLoading.value = true

  try {
    const response = await api.liveMonitor.getSessionById(sessionId)
    selectedMessages.value = normalizeMessages(response)
  } catch (_) {
    selectedMessages.value = []
  } finally {
    transcriptLoading.value = false
  }
}

watch([sessionFilter, sortMode], () => {
  sessionPage.value = 1
  refresh({ preserveTranscript: true })
})

function goToPage(page) {
  const nextPage = Math.min(Math.max(page, 1), pagination.value.totalPages || 1)
  if (nextPage !== sessionPage.value) {
    sessionPage.value = nextPage
    refresh({ preserveTranscript: true })
  }
}

function selectSession(sessionId) {
  selectedSessionId.value = sessionId
  loadSessionTranscript(sessionId)
}

function normalizeList(data, keys = []) {
  if (Array.isArray(data)) return data
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key]
  }
  return []
}

function normalizeMessages(data) {
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.messages)
    ? data.messages
    : []

  return raw
    .map(message => ({
      id: message.id,
      sender: message.sender || 'unknown',
      text: message.message || '',
      timestamp: message.timestamp || message.created_at || null
    }))
    .sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0
      return aTime - bTime
    })
}

function avatarLabel(sender) {
  if (sender === 'assistant') return 'AI'
  if (sender === 'user') return 'CL'
  if (sender === 'system') return 'SYS'
  return '?'
}

function roleLabel(sender) {
  if (sender === 'assistant') return 'Agent'
  if (sender === 'user') return 'Caller'
  if (sender === 'system') return 'System'
  return 'Unknown'
}

function formatDate(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function displayValue(value) {
  return value && String(value).trim() ? value : '—'
}

function sessionLabel(session) {
  return session.session_label || session.name || session.phone || session.email || `Session #${session.id}`
}

function buildTimeline() {
  const session = selectedSession.value
  const items = []

  const pushAction = (key, title, description, at) => {
    if (items.some(item => item.key === key)) return

    items.push({
      key,
      uid: `${key}-${at || 'na'}`,
      title,
      description,
      at: at || new Date().toISOString(),
      timeLabel: formatTime(at || new Date().toISOString())
    })
  }

  if (session?.started_at) {
    pushAction('session-start', 'Call started', `Live session #${session.id} started.`, session.started_at)
  }

  if (selectedSessionMeta.value.name !== '—') {
    pushAction('caller-name', 'Caller identified', `Captured caller name: ${selectedSessionMeta.value.name}.`, session?.started_at)
  }

  if (selectedSessionMeta.value.phone !== '—') {
    pushAction('caller-phone', 'Phone captured', `Captured phone number: ${selectedSessionMeta.value.phone}.`, session?.started_at)
  }

  if (selectedSessionMeta.value.service !== '—') {
    pushAction('service', 'Service captured', `Requested service: ${selectedSessionMeta.value.service}.`, session?.started_at)
  }

  if (selectedSession.value?.scheduled_at) {
    pushAction('appointment', 'Appointment captured', `Appointment targeted for ${selectedSessionMeta.value.appointment}.`, selectedSession.value.scheduled_at)
  }

  if (bookingStatus.value === 'Booked') {
    pushAction('booking', 'Booking created', 'The appointment appears to be booked successfully.', selectedSession.value?.scheduled_at || session?.started_at)
  }

  if (calendarStatus.value === 'Synced') {
    pushAction('calendar', 'Calendar synced', 'Calendar synchronization completed.', selectedSession.value?.scheduled_at || session?.started_at)
  }

  if (depositStatus.value === 'Requested') {
    pushAction('deposit-requested', 'Deposit requested', 'The deposit flow was triggered during the call.', selectedSession.value?.scheduled_at || session?.started_at)
  }

  if (session?.ended_at) {
    pushAction('session-end', 'Call ended', 'The live session is completed.', session.ended_at)
  }

  return items.sort((a, b) => {
    const aTime = new Date(a.at || 0).getTime() || 0
    const bTime = new Date(b.at || 0).getTime() || 0
    return bTime - aTime
  })
}

function formatTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.session-meta-row{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:14px}.meta-chip{padding:12px;border-radius:10px;background:#edf7f1;border:1px solid #d8eadf}.meta-chip span{display:block;color:#6b756f;font-size:12px;font-weight:900;text-transform:uppercase}.meta-chip strong{display:block;margin-top:6px;word-break:break-word}.transcript-scroll{max-height:540px;overflow:auto;padding-right:4px}.timeline-list{display:grid;gap:12px}.timeline-item{display:flex;gap:12px;align-items:flex-start;padding:12px;border-radius:10px;background:#f7faf8;border:1px solid #e3ebe5}.timeline-dot{width:10px;height:10px;margin-top:5px;border-radius:999px;background:#0d8f62;flex:0 0 auto}.timeline-body{flex:1}.timeline-top{display:flex;justify-content:space-between;gap:10px;align-items:flex-start;font-size:12px;color:#66736b;margin-bottom:4px}.timeline-body p{margin:0;color:#526159;line-height:1.55}.agent-card{display:grid;gap:10px;padding:14px;border-radius:10px;background:#f7faf8;border:1px solid #e3ebe5}.agent-card strong{font-size:1.05rem}.agent-card p{color:#526159;line-height:1.55}.agent-meta{display:flex;gap:8px;flex-wrap:wrap}.agent-meta span{padding:6px 10px;border-radius:999px;background:#edf7f1;color:#0d8f62;font-size:12px;font-weight:800}
.monitor-page{min-height:100vh;padding:32px;background:linear-gradient(135deg,#f7faf8 0%,#eef5ef 55%,#f8fafc 100%);color:#17211b}.page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;margin-bottom:22px}.eyebrow{margin:0 0 8px;color:#0d8f62;font-size:12px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}.subtitle{max-width:760px;margin-top:8px;color:#66736b;line-height:1.6}h1,h2,p{margin:0}.page-header h1{font-size:clamp(2rem,4vw,3.2rem);letter-spacing:-.04em}.header-actions{display:flex;gap:12px;align-items:center;flex-wrap:wrap}.ghost-btn{display:inline-flex;align-items:center;gap:8px;min-height:42px;padding:0 14px;border:1px solid #dce5df;border-radius:8px;background:#fff;color:#17211b;font-weight:900;cursor:pointer}.toggle{display:inline-flex;align-items:center;gap:8px;padding:10px 12px;border-radius:8px;background:#fff;border:1px solid #dce5df;font-weight:800;color:#526159}.toggle input{accent-color:#0d8f62}.kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;margin-bottom:18px}.kpi-card,.panel,.error-banner{background:#fff;border:1px solid #e3ebe5;border-radius:12px;box-shadow:0 18px 50px rgba(15,23,42,.05)}.kpi-card{padding:18px}.kpi-card span{color:#6b756f;font-size:13px;font-weight:800}.kpi-card strong{display:block;margin-top:8px;font-size:2rem;letter-spacing:-.04em}.main-grid{display:grid;grid-template-columns:minmax(280px,340px) minmax(0,1fr) minmax(280px,330px);gap:18px;align-items:start}.panel{padding:18px}.panel-header{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;margin-bottom:16px}.panel-header p{margin-top:4px;color:#66736b;font-size:14px}.pill{display:inline-flex;align-items:center;justify-content:center;min-width:34px;height:34px;border-radius:999px;background:#edf7f1;color:#0d8f62;font-weight:900}.filters-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px}.filters-row label{display:grid;gap:6px}.filters-row span{color:#6b756f;font-size:12px;font-weight:900;text-transform:uppercase}.filters-row select{width:100%;min-height:40px;padding:0 12px;border:1px solid #dce5df;border-radius:8px;background:#fff;color:#17211b}.loading-state,.empty-state{padding:30px 16px;border:1px dashed #d7e0da;border-radius:10px;color:#66736b;text-align:center}.session-list{display:grid;gap:12px;max-height:560px;overflow:auto;padding-right:4px}.message-list{display:grid;gap:12px}.session-card{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;padding:14px;border:1px solid #e3ebe5;border-radius:10px;background:#fbfdfb;text-align:left;cursor:pointer;transition:transform .15s ease,border-color .15s ease}.session-card:hover,.session-card.selected{border-color:#0d8f62;transform:translateY(-1px)}.session-card p,.session-time{margin-top:4px;color:#66736b;font-size:13px}.message-row{display:flex;gap:12px;align-items:flex-start}.message-row.user{flex-direction:row-reverse}.avatar{width:38px;height:38px;display:flex;align-items:center;justify-content:center;border-radius:8px;background:#edf2ee;color:#17211b;font-weight:900;flex:0 0 auto}.message-card{flex:1;padding:14px;border-radius:10px;background:#f7faf8;border:1px solid #e3ebe5}.message-row.assistant .message-card{background:#eef7f1}.message-row.user .message-card{background:#17211b;color:#fff}.message-row.user .message-card span{color:rgba(255,255,255,.72)}.message-top{display:flex;justify-content:space-between;gap:12px;color:#66736b;font-size:12px;margin-bottom:6px}.message-card p{white-space:pre-wrap;word-break:break-word;line-height:1.55}.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}.detail-card{padding:12px;border-radius:10px;background:#f7faf8}.detail-card span{display:block;color:#6b756f;font-size:12px;font-weight:900;text-transform:uppercase}.detail-card strong{display:block;margin-top:6px;word-break:break-word}.error-banner{margin-bottom:18px;padding:14px 16px;background:#fff7f7;border-color:#fecaca;color:#b91c1c;font-weight:800}.pagination-row{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:14px;padding-top:14px;border-top:1px solid #eef2ef;color:#526159}.pager-btn{min-height:38px;padding:0 12px;border:1px solid #dce5df;border-radius:8px;background:#fff;color:#17211b;font-weight:900;cursor:pointer}.pager-btn:disabled{opacity:.5;cursor:not-allowed}.spin{animation:spin .9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}@media(max-width:1100px){.main-grid{grid-template-columns:1fr 1fr}.detail-panel{grid-column:1/-1}}@media(max-width:760px){.monitor-page{padding:18px 12px}.page-header{flex-direction:column}.kpi-grid,.main-grid,.filters-row{grid-template-columns:1fr}.header-actions,.ghost-btn,.toggle{width:100%}.ghost-btn{justify-content:center}.session-card,.message-row{flex-direction:column}.message-row.user{flex-direction:column-reverse}.detail-grid{grid-template-columns:1fr}.session-list{max-height:420px}.pagination-row{flex-wrap:wrap}}
</style>
